/**
 * Onboarding store — drives the signup funnel: plan select → register →
 * payment → success.
 *
 * BUG FIX (Day 14): the file previously had a duplicate `import { create }`
 * statement, with one glued to the end of a comment with no newline. ESLint
 * was warning about it; this version cleans up the imports.
 *
 * UX UPGRADE (Day 14): added `selectedPlanIntent` + `setSelectedPlanIntent`.
 * Landing-page CTAs can write the user's chosen plan/billing to the store
 * BEFORE creating a backend session, so /register can preload that choice
 * instead of asking the user to pick again. The query string remains the
 * canonical source (refresh-safe, shareable), but the store hydrates from
 * it so the same code path works for both navigation types.
 *
 * Intent vs session: `selectedPlanIntent` is just a remembered preference
 * — no backend roundtrip. `selectedPlan` is set by `selectPlan()` after
 * /onboarding/initialize succeeds and means "we have a real session."
 */
import { create } from "zustand";
import axios from "axios";

interface PlanData {
  planName: string;
  billingCycle: string;
  duration: string;
}

interface RegistrationData {
  name: string;
  email: string;
  password: string;
  companyName: string;
  companyEmail?: string;
  companyAddress?: string;
  companyPhone?: string;
  industryType?: string;
  /** ISO country code — sets the company and business currency. */
  country?: string;
  /** Referral code of the aeTech person who brought the customer in. */
  referralCode?: string;
}

interface PaymentData {
  method: "mobile_money" | "credit_card" | "bank_transfer";
  paymentDetails: {
    phone?: string;
  };
}

/**
 * Pre-session plan intent — what the user picked on the landing page.
 * Persisted ONLY in the store (no backend call yet). Used by /register
 * to preload the subscription step.
 */
export interface SelectedPlanIntent {
  /** Frontend plan id: "start" | "growth" | "business" — never "enterprise" */
  planId: string;
  /** Backend enum: "Basic" | "Standard" | "Premium" */
  backendEnum: string;
  /** "monthly" | "yearly" */
  billingCycle: string;
}

interface OnboardingState {
  currentStep: "plan" | "registration" | "payment" | "success";
  isLoading: boolean;
  error: string | null;
  sessionId: string | null;
  /** Real, server-acknowledged plan (set after /onboarding/initialize) */
  selectedPlan: {
    name: string;
    duration: string;
    price: number;
  } | null;
  /** Pre-session intent from the landing page (no backend call) */
  selectedPlanIntent: SelectedPlanIntent | null;
  paymentDetails: {
    authorizationUrl?: string;
    reference?: string;
  } | null;
  isPaymentVerified: boolean;

  // Actions
  setSelectedPlanIntent: (intent: SelectedPlanIntent | null) => void;
  selectPlan: (planData: PlanData) => Promise<boolean>;
  submitRegistration: (data: RegistrationData, logo?: File) => Promise<boolean>;
  initiatePayment: (paymentData: PaymentData) => Promise<boolean>;
  verifyPayment: (reference: string) => Promise<boolean>;
  /** Create the account on a 7-day free trial (no payment). */
  startTrial: () => Promise<boolean>;
  reset: () => void;
}

const API_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  currentStep: "plan",
  isLoading: false,
  error: null,
  sessionId: null,
  selectedPlan: null,
  selectedPlanIntent: null,
  paymentDetails: null,
  isPaymentVerified: false,

  // ─── New: lightweight intent setter for landing → register handoff ─
  setSelectedPlanIntent: (intent) => {
    set({ selectedPlanIntent: intent });
  },

  selectPlan: async (planData) => {
    try {
      set({ isLoading: true, error: null });
      const response = await axios.post(
        `${API_URL}/onboarding/initialize`,
        planData,
      );

      if (response.data.success) {
        set({
          sessionId: response.data.data.sessionId,
          selectedPlan: response.data.data.plan,
          currentStep: "registration",
        });
        return true;
      } else {
        throw new Error(response.data.message || "Failed to select plan");
      }
    } catch (error) {
      const msg =
        (axios.isAxiosError(error) && error.response?.data?.message) ||
        (error instanceof Error ? error.message : "Failed to select plan");
      set({ error: msg });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  submitRegistration: async (data, logo) => {
    try {
      set({ isLoading: true, error: null });
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      if (logo) {
        formData.append("companyLogo", logo);
      }

      const response = await axios.post(
        `${API_URL}/onboarding/${get().sessionId}/register`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        set({
          currentStep: "payment",
          selectedPlan: {
            ...get().selectedPlan!,
            price: response.data.data.paymentDetails.amount,
          },
        });
        return true;
      } else {
        throw new Error(
          response.data.message || "Failed to submit registration",
        );
      }
    } catch (error) {
      const msg =
        (axios.isAxiosError(error) && error.response?.data?.message) ||
        (error instanceof Error
          ? error.message
          : "Failed to submit registration");
      set({ error: msg });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  initiatePayment: async (paymentData) => {
    try {
      set({ isLoading: true, error: null });
      const sessionId = get().sessionId;

      if (!sessionId) {
        throw new Error(
          "Session ID is missing. Please restart the registration process.",
        );
      }

      const response = await axios.post(
        `${API_URL}/onboarding/${sessionId}/payment`,
        paymentData,
      );

      if (response.data.success) {
        const { authorizationUrl, reference } = response.data.data;
        set({
          paymentDetails: {
            authorizationUrl,
            reference,
          },
        });

        if (paymentData.method === "mobile_money" && authorizationUrl) {
          localStorage.setItem("payment_reference", reference);
          localStorage.setItem("onboarding_session_id", sessionId);

          const paystackWindow = window.open(authorizationUrl, "_blank");

          const handleMessage = (event: MessageEvent) => {
            if (
              event.data.status === "success" ||
              event.data.status === "cancelled"
            ) {
              if (paystackWindow && !paystackWindow.closed) {
                paystackWindow.close();
              }
              window.removeEventListener("message", handleMessage);
              window.location.href = `/payment-verification?reference=${reference}`;
            }
          };

          window.addEventListener("message", handleMessage);

          const checkPaymentStatus = async () => {
            try {
              const currentSessionId = localStorage.getItem(
                "onboarding_session_id",
              );
              if (!currentSessionId) {
                console.error(
                  "Session ID is missing during payment verification",
                );
                return false;
              }

              const verifyResponse = await axios.get(
                `${API_URL}/onboarding/verify-payment/${currentSessionId}?reference=${reference}`,
              );

              if (verifyResponse.data.success) {
                if (paystackWindow && !paystackWindow.closed) {
                  paystackWindow.close();
                }
                window.location.href = `/payment-verification?reference=${reference}`;
                return true;
              }
            } catch (error) {
              console.error("Error checking payment status:", error);
            }
            return false;
          };

          const pollInterval = setInterval(async () => {
            const isCompleted = await checkPaymentStatus();
            if (isCompleted) {
              clearInterval(pollInterval);
            }
          }, 3000);

          setTimeout(
            () => {
              clearInterval(pollInterval);
            },
            5 * 60 * 1000,
          );
        }
        return true;
      } else {
        throw new Error(response.data.message || "Failed to initiate payment");
      }
    } catch (error) {
      const msg =
        (axios.isAxiosError(error) && error.response?.data?.message) ||
        (error instanceof Error ? error.message : "Failed to initiate payment");
      set({ error: msg });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  startTrial: async () => {
    try {
      set({ isLoading: true, error: null });
      const sessionId =
        get().sessionId || localStorage.getItem("onboarding_session_id");
      if (!sessionId) {
        throw new Error("Your sign-up session has expired. Please start again.");
      }
      const response = await axios.post(
        `${API_URL}/onboarding/${sessionId}/start-trial`,
      );
      if (response.data?.success) {
        set({ currentStep: "success", error: null });
        localStorage.removeItem("onboarding_session_id");
        return true;
      }
      throw new Error(response.data?.message || "Could not start your free trial");
    } catch (error) {
      const msg =
        (axios.isAxiosError(error) &&
          (error.response?.data?.message || error.response?.data?.error)) ||
        (error instanceof Error ? error.message : "Could not start your free trial");
      set({ error: msg });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  verifyPayment: async (reference) => {
    try {
      set({ isLoading: true, error: null });

      const sessionId =
        localStorage.getItem("onboarding_session_id") || get().sessionId;

      if (!sessionId) {
        throw new Error(
          "Session ID is missing. Please restart the registration process.",
        );
      }

      if (!reference) {
        throw new Error("Payment reference is missing");
      }

      // Check if we already have tokens (payment already verified)
      const existingAccessToken = localStorage.getItem("accessToken");
      const existingRefreshToken = localStorage.getItem("refreshToken");

      if (existingAccessToken && existingRefreshToken) {
        set({
          currentStep: "success",
          isPaymentVerified: true,
          error: null,
        });
        return true;
      }

      const response = await axios.get(
        `${API_URL}/onboarding/verify-payment/${sessionId}?reference=${reference}`,
      );

      if (!response.data) {
        throw new Error("Invalid response from server");
      }

      if (response.data.success === true && response.data.data) {
        const { accessToken, refreshToken } = response.data.data;

        if (!accessToken || !refreshToken) {
          throw new Error("Missing authentication tokens in response");
        }

        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.removeItem("payment_reference");

        set({
          currentStep: "success",
          isPaymentVerified: true,
          error: null,
        });
        return true;
      }

      throw new Error(
        response.data.message ||
          response.data.error ||
          "Payment verification failed",
      );
    } catch (error) {
      console.error("Payment verification error:", error);

      const existingAccessToken = localStorage.getItem("accessToken");
      const existingRefreshToken = localStorage.getItem("refreshToken");

      if (existingAccessToken && existingRefreshToken) {
        set({
          currentStep: "success",
          isPaymentVerified: true,
          error: null,
        });
        return true;
      }

      let errorMessage = "Payment verification failed";
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 404) {
          errorMessage =
            "Payment session not found. Please try again or contact support.";
        } else if (error.response.status === 401) {
          errorMessage = "Payment session expired. Please try again.";
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({
        error: errorMessage,
        isPaymentVerified: false,
      });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  reset: () => {
    set({
      currentStep: "plan",
      isLoading: false,
      error: null,
      sessionId: null,
      selectedPlan: null,
      // selectedPlanIntent intentionally preserved across reset so a
      // returning user from /book-demo still sees their original choice.
      paymentDetails: null,
      isPaymentVerified: false,
    });
  },
}));
